import type { InputParser } from "../types";

const formatSsn = (digits: string): string => {
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

const formatMaskedSsn = (digits: string): string => {
  if (digits.length < 9) return formatSsn(digits);
  return `\u2022\u2022\u2022-\u2022\u2022-${digits.slice(5, 9)}`;
};

interface SsnOptions {
  masked?: boolean;
  asString?: boolean;
}

function ssnParser(opts?: { masked?: boolean }): InputParser<number | null>;
function ssnParser(opts: {
  masked?: boolean;
  asString: true;
}): InputParser<string>;
function ssnParser(
  opts?: SsnOptions
): InputParser<number | null> | InputParser<string> {
  const masked = opts?.masked ?? false;
  const asString = opts?.asString ?? false;
  const fmt = masked ? formatMaskedSsn : formatSsn;

  if (asString) {
    return {
      inputMode: "numeric",
      validate: (raw) => raw === "" || /^\d{0,9}$/.test(raw),
      parse: (raw) => raw.replace(/\D/g, ""),
      format: (value: string) => {
        if (value === "") return "";
        return fmt(value);
      },
      toEditable: (value: string) => value
    } as InputParser<string>;
  }

  return {
    inputMode: "numeric",
    validate: (raw) => raw === "" || /^\d{0,9}$/.test(raw),
    parse: (raw) => {
      const digits = raw.replace(/\D/g, "");
      if (digits === "") return null;
      return parseInt(digits, 10);
    },
    format: (value: number | null) => {
      if (value === null) return "";
      return fmt(String(value));
    },
    toEditable: (value: number | null) => {
      if (value === null) return "";
      return String(value);
    }
  };
}

export { ssnParser };
